/* eslint-disable @next/next/no-img-element */
import { useCallback, useRef, useState } from 'react';

import clsx from 'clsx';

import { ThemeType } from '@lessonPlanner/constants';

import { Button } from '../Button';
import { SkeletalRefLoaderMini } from '../SkeletalRefLoader';

import { THEME_CATEGORIES } from '../../constants/pptThemeCatgories';

import { Label } from './utils/Label';

interface ThemeOption {
  id: string;
  name: string;
  backgroundImageTitle?: string;
  [key: string]: unknown;
}

interface CollapsibleThemePanelProps {
  value: ThemeOption;
  onChange: (value: ThemeOption) => void;
  options: ThemeOption[];
  label?: React.ReactNode;
  errorMessage?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
  slideTitle?: string;
}

const LOADING_BACKGROUND_COLOR = '#e5e7eb';
const DEFAULT_THEME_BACKGROUND = '#FFFFFF';

export function CollapsibleThemePanel({
  value,
  onChange,
  options,
  label,
  errorMessage,
  id,
  className,
  disabled = false,
  slideTitle,
}: CollapsibleThemePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Group options by category
  const categorizedOptions = Object.values(THEME_CATEGORIES).map((category) => {
    const themesInCategory = options.filter((option) =>
      category.themes.includes(option.id as ThemeType),
    );
    return {
      category: category.label,
      themes: themesInCategory,
    };
  });

  // Add image loading state per theme id
  const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});

  // Function to get background color or image style
  const getThemeBackgroundStyle = useCallback((theme: ThemeOption) => {
    if (theme.backgroundImageTitle) {
      return {
        backgroundImage: `url(${theme.backgroundImageTitle})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }

    // For themes without background images, use background color
    return {
      backgroundColor: (theme.background as string) || '#FFFFFF',
    };
  }, []);

  return (
    <div ref={panelRef} className={clsx('w-full', className)}>
      {label && <Label id={id}>{label}</Label>}

      <div className="w-full">
        {/* Theme Selection Panel */}
        {categorizedOptions.map(({ category, themes }) =>
          themes.length > 0 ? (
            <div key={category} className="mb-3">
              <div className="mb-1 border-b border-gray-200 bg-blue-50 pl-4 pb-1 text-left text-sm font-medium text-gray-900">
                {category}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {themes.map((theme) => (
                  <Button
                    key={theme.id}
                    disabled={disabled}
                    className={clsx(
                      'flex flex-col items-center rounded-md p-2 transition-colors hover:bg-blue-50 focus:outline-none',
                      value.id === theme.id
                        ? 'bg-blue-100 ring-2 ring-blue-500'
                        : 'bg-white',
                      'focus:bg-blue-50 focus:ring-2 focus:ring-blue-500',
                    )}
                    onClick={() => onChange(theme)}
                  >
                    <div className="relative mb-2 aspect-video w-full overflow-hidden rounded shadow-sm">
                      <div
                        className="absolute inset-0 h-full w-full"
                        style={
                          theme.backgroundImageTitle && imageLoading[theme.id]
                            ? { backgroundColor: LOADING_BACKGROUND_COLOR }
                            : getThemeBackgroundStyle(theme)
                        }
                      >
                        {theme.backgroundImageTitle && (
                          <>
                            {imageLoading[theme.id] && (
                              <SkeletalRefLoaderMini />
                            )}
                            <img
                              ref={(img) => {
                                if (
                                  img &&
                                  typeof imageLoading[theme.id] === 'undefined'
                                ) {
                                  setImageLoading((prev) => ({
                                    ...prev,
                                    [theme.id]: true,
                                  }));
                                }
                              }}
                              alt=""
                              className="hidden"
                              src={theme.backgroundImageTitle}
                              onError={() =>
                                setImageLoading((prev) => ({
                                  ...prev,
                                  [theme.id]: false,
                                }))
                              }
                              onLoad={() =>
                                setImageLoading((prev) => ({
                                  ...prev,
                                  [theme.id]: false,
                                }))
                              }
                            />
                          </>
                        )}
                        {slideTitle &&
                          (!theme.backgroundImageTitle ||
                            !imageLoading[theme.id]) && (
                            <div
                              className="absolute inset-0 flex items-center justify-center text-[0.6rem] font-semibold"
                              style={{
                                color:
                                  (theme.text as string) ||
                                  DEFAULT_THEME_BACKGROUND,
                              }}
                            >
                              {slideTitle}
                            </div>
                          )}
                      </div>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="line-clamp-1 text-center text-xs font-medium">
                        {theme.name}
                      </span>
                    </div>
                  </Button>
                ))}
              </div>
            </div>
          ) : null,
        )}
      </div>

      {errorMessage && (
        <p className="mt-1 text-sm text-red-500">{errorMessage}</p>
      )}
    </div>
  );
}
